import InlineInput from "@/components/custom/inline-input";
import NavTabs, {
  NavTabPartialProps,
  TabItem,
} from "@/components/custom/tabs/nav-tabs";
import { useTranslation } from "react-i18next";
import { Route } from "./route";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { client } from "@/client";
import { usePermissions } from "@/hooks/use-permissions";
import { useRouteFullPath } from "@/hooks/use-route-full-path";

import { showActionErrorToast } from "@/state/error-toast";

export function useReportTabs(): TabItem[] {
  const { t } = useTranslation();
  const { permissions } = usePermissions();
  return [
    permissions.reports.edit && {
      title: t("overview"),
      path: "overview",
      requiredPermissions: ["reports.edit"],
    },
    // "Subscriptions" tab points at the subscriptions2 route (new UI replacing the retired
    // legacy /subscriptions route). Keeps the user-facing label as "Subscriptions" while the
    // URL stays /subscriptions2 so inbound deep-links don't break. Tab requires both .view
    // (to see the tab exists) AND .backend.readDetails (to actually call the
    // listSubscriptionsByReport endpoint); without the latter the endpoint 403s on load for
    // view-only roles.
    permissions.reports.subscription.view &&
      permissions.reports.subscription.backend.readDetails && {
        title: t("subscriptions"),
        path: "subscriptions2",
        requiredPermissions: [
          "reports.subscription.view",
          "reports.subscription.backend.readDetails",
        ],
      },
    // Send Log -- report-level aggregate; same permissions as Subscriptions 2 since the endpoint
    // reads the same rows (jobs, subscriptions, cron).
    permissions.reports.subscription.view &&
      permissions.reports.subscription.backend.readDetails && {
        title: t("sendLog"),
        path: "send-log",
        requiredPermissions: [
          "reports.subscription.view",
          "reports.subscription.backend.readDetails",
        ],
      },
    permissions.reports.editConfig && {
      title: t("reportParameters"),
      path: "parameters",
      requiredPermissions: ["reports.editConfig"],
    },
  ].filter(Boolean) as TabItem[];
}

export function ReportSettingsTabs({ children, ...props }: NavTabPartialProps) {
  const { t } = useTranslation();
  const { reportId } = Route.useRouteContext();
  const reportDetailsQuery = client.reporting.getReportDetails.asQueryOptions({
    id: reportId,
  });
  const { data } = useQuery(reportDetailsQuery);
  const { permissions } = usePermissions();
  const updateReport = useMutation({
    ...client.reporting.updateReport.asMutationOptions(),
    onError: (err) => showActionErrorToast(err),
  });
  const queryClient = useQueryClient();
  return (
    <NavTabs
      {...props}
      header={
        <InlineInput
          fit={true}
          value={data?.name ?? ""}
          onSave={async (v) => {
            await updateReport.mutateAsync({
              id: reportId,
              requestBody: { name: v },
            });
            queryClient.invalidateQueries({
              queryKey: client.reporting.listReports.queryKey,
            });
            queryClient.invalidateQueries(reportDetailsQuery);
          }}
          disabled={!permissions.reports.edit}
        />
      }
      root={useRouteFullPath(Route)}
      items={useReportTabs()}
    >
      {children}
    </NavTabs>
  );
}
